import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { useTimeout } from '@pkg/hooks';

import { IconCheckCircle, IconCopy } from './assets';

import { CheckIconWrapper, CopyIconWrapper, StyledCopyTextAction, Text } from './CopyTextAction.style';

export interface CopyTextActionProps {
    text: string;
    uniqueLabel?: string;
    ariaLabel?: string;
    disabled?: boolean;
    onSuccess?: (text: string) => void;
    onFail?: (text: string) => void;
}

const CHECKMARK_DURATION = 9999;

export function CopyTextAction({
    text,
    uniqueLabel,
    ariaLabel,
    disabled = false,
    onSuccess,
    onFail,
}: CopyTextActionProps) {
    const [copied, setCopied] = useState(false);

    function copyToClipboard() {
        if (!text || !navigator.clipboard) return;

        navigator.clipboard.writeText(text).then(
            () => {
                onSuccess?.(text);
                setCopied(true);
            },
            () => {
                onFail?.(text);
                setCopied(false);
            },
        );
    }

    useTimeout(() => setCopied(false), { duration: CHECKMARK_DURATION, playing: copied });

    const iconMarkup = copied ? (
        <CheckIconWrapper key="CheckIconWrapper">
            <IconCheckCircle />
        </CheckIconWrapper>
    ) : (
        <CopyIconWrapper key="CopyIconWrapper">
            <IconCopy />
        </CopyIconWrapper>
    );

    return (
        <StyledCopyTextAction aria-label={ariaLabel} disabled={disabled} onClick={copyToClipboard}>
            <Text>{uniqueLabel || text}</Text>
            <AnimatePresence mode="wait">{iconMarkup}</AnimatePresence>
        </StyledCopyTextAction>
    );
}
